import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import Icon from '../../components/AppIcon';

import QRCodePreview from './components/QRCodePreview';
import CustomizationPanel from './components/CustomizationPanel';
import DownloadPanel from './components/DownloadPanel';
import { useLanguage } from '../../i18n/LanguageContext';
import { fetchRestaurantProfile, subscribeToRestaurantProfile } from '../../services/menuDataService';
import useEffectiveRestaurantId from '../../hooks/useEffectiveRestaurantId';
import { useAuth } from '../../contexts/AuthContext';
import { buildRestaurantBaseUrl } from '../../utils/appUrl';
import { supabase } from '../../lib/supabase';

const QRCodeGenerator = () => {
  const { t } = useLanguage();
  const restaurantId = useEffectiveRestaurantId();
  const { restaurantSlug } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [restaurantProfile, setRestaurantProfile] = useState(null);
  const [netlifyDomain, setNetlifyDomain] = useState(null);

  // Derive the effective slug: prefer auth slug, fall back to profile slug
  const effectiveSlug = restaurantSlug || restaurantProfile?.slug || null;

  const buildMenuUrl = (slug, domain) => {
    if (domain) {
      return `https://${domain}?source=qr`;
    }
    return slug ? `${buildRestaurantBaseUrl(slug)}?source=qr` : 'https://scann3r.ca?source=qr';
  };

  const [qrSettings, setQrSettings] = useState({
    restaurantName: 'Gourmet Bistro',
    menuUrl: 'https://scann3r.ca?source=qr',
    color: 'primary',
    size: 'large',
    includeLogo: true,
    addBorder: true,
    enableAR: true,
    showPrices: true,
    enableSearch: true,
    promoMessage: '',
    expirationDate: ''
  });

  const fetchNetlifyDomain = async (restId) => {
    if (!restId) return null;
    const { data, error } = await supabase
      ?.from('restaurants')
      ?.select('netlify_domain')
      ?.eq('id', restId)
      ?.maybeSingle();
    if (error) {
      console.error('Failed to fetch netlify_domain:', error);
      return null;
    }
    return data?.netlify_domain || null;
  };

  useEffect(() => {
    if (!restaurantId) return;

    const loadData = async () => {
      const [profile, domain] = await Promise.all([
        fetchRestaurantProfile(restaurantId)?.catch((err) => {
          console.error('Failed to fetch restaurant profile:', err);
          return null;
        }),
        fetchNetlifyDomain(restaurantId),
      ]);

      if (profile) setRestaurantProfile(profile);
      if (domain !== undefined) setNetlifyDomain(domain);

      const slug = restaurantSlug || profile?.slug || null;
      setQrSettings(prev => ({
        ...prev,
        restaurantName: profile?.name || prev?.restaurantName,
        menuUrl: buildMenuUrl(slug, domain),
      }));
    };

    loadData();

    const subscription = subscribeToRestaurantProfile(() => {
      loadData();
    }, restaurantId);

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [restaurantId, restaurantSlug]);

  // Also update menuUrl when restaurantSlug becomes available after initial load
  useEffect(() => {
    if (!restaurantSlug && !netlifyDomain) return;
    setQrSettings(prev => ({
      ...prev,
      menuUrl: buildMenuUrl(restaurantSlug, netlifyDomain),
    }));
  }, [restaurantSlug, netlifyDomain]);

  const mockQRCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=800x800&data=${encodeURIComponent(qrSettings?.menuUrl || 'https://scann3r.ca?source=qr')}`;

  const handleSettingsChange = (newSettings) => {
    setQrSettings(newSettings);
    setHasChanges(true);
  };

  const handleGenerateQR = () => {
    console.log('Generating QR code with settings:', qrSettings);
    setHasChanges(false);
  };

  const handleDownload = (format, size) => {
    console.log(`Downloading QR code as ${format}${size ? ` (${size})` : ''}`);
  };

  const handleTest = () => {
    console.log('Opening test modal for QR code scanning');
  };

  const handleSave = () => {
    console.log('Saving QR code configuration');
    setHasChanges(false);
  };

  const handleShare = () => {
    console.log('Opening share options');
  };

  const handleDuplicate = () => {
    console.log('Duplicating current QR code configuration');
  };

  const handleReset = () => {
    setQrSettings({
      restaurantName: restaurantProfile?.name || 'Gourmet Bistro',
      menuUrl: buildMenuUrl(effectiveSlug, netlifyDomain),
      color: 'primary',
      size: 'large',
      includeLogo: true,
      addBorder: true,
      enableAR: true,
      showPrices: true,
      enableSearch: true,
      promoMessage: '',
      expirationDate: ''
    });
    setHasChanges(false);
  };

  return (
    <div className="qr-generator-page min-h-screen bg-background">
      <AdminSidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        activeTab="qr-code"
        onTabChange={() => {}}
      />
      <div className={`qr-generator-content transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="page-header mb-6 md:mb-8 lg:mb-10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  {t('qrCodeGenerator')}
                </h1>
                <p className="text-sm md:text-base lg:text-lg text-muted-foreground font-caption">
                  {t('createCustomize')}
                </p>
              </div>
            </div>
          </div>

          <div className="generator-layout grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-10">
            <div className="left-column space-y-6 md:space-y-8">
              <QRCodePreview
                qrCodeUrl={mockQRCodeUrl}
                restaurantName={restaurantProfile?.name || qrSettings?.restaurantName}
                restaurantLogo={restaurantProfile?.logo || ''}
                restaurantLogoAlt={restaurantProfile?.logoAlt || 'Restaurant logo'}
                size={qrSettings?.size}
                showBorder={qrSettings?.addBorder}
                includeLogo={qrSettings?.includeLogo}
              />

              <DownloadPanel
                qrCodeUrl={mockQRCodeUrl}
                restaurantName={restaurantProfile?.name || qrSettings?.restaurantName}
                restaurantProfile={restaurantProfile}
                qrSettings={qrSettings}
                onDownload={handleDownload}
                onTest={handleTest}
              />
            </div>

            <div className="right-column">
              <CustomizationPanel
                settings={qrSettings}
                onSettingsChange={handleSettingsChange}
                onGenerateQR={handleGenerateQR}
              />
            </div>
          </div>

          <div className="usage-guide mt-8 md:mt-10 lg:mt-12 bg-card rounded-xl p-6 md:p-8 shadow-warm-lg">
            <div className="flex items-start space-x-4 mb-6">
              <div className="bg-accent/10 p-3 rounded-lg flex-shrink-0">
                <Icon name="Lightbulb" size={28} color="var(--color-accent)" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-2">
                  {t('bestPractices')}
                </h2>
                <p className="text-sm md:text-base text-muted-foreground font-caption">
                  {t('bestPracticesDesc')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="practice-item">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-base font-heading font-medium text-foreground mb-1">
                      {t('optimalPlacement')}
                    </h3>
                    <p className="text-sm text-muted-foreground font-caption">
                      {t('optimalPlacementDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="practice-item">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-base font-heading font-medium text-foreground mb-1">
                      {t('sizeMatters')}
                    </h3>
                    <p className="text-sm text-muted-foreground font-caption">
                      {t('sizemattersDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="practice-item">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-base font-heading font-medium text-foreground mb-1">
                      {t('clearInstructions')}
                    </h3>
                    <p className="text-sm text-muted-foreground font-caption">
                      {t('clearInstructionsDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="practice-item">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-base font-heading font-medium text-foreground mb-1">
                      {t('regularTesting')}
                    </h3>
                    <p className="text-sm text-muted-foreground font-caption">
                      {t('regularTestingDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default QRCodeGenerator;